import { Card, CardHeader, Link, makeStyles, Text, Title3 } from "@fluentui/react-components";
import { ArrowRight16Regular, Cloud48Regular, Shield48Regular, Apps48Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { dolphinTokens } from "../../utils/Theme";

const useStyles = makeStyles({
    row: {
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "1rem",
        marginBottom: "3rem",
        "@media (min-width: 800px) and (max-width: 1250px)": {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2rem",
            justifyItems: "center",
        },
    },
    cardCol: {
        flex: "1 1 300px",
        maxWidth: "350px",
        width: "100%",
        padding: "0 1rem",
        "@media (min-width: 800px) and (max-width: 1250px)": {
            maxWidth: "100%",
            width: "100%",
            padding: "0",
        },
    },
    card: {
        display: "flex !important",
        flexDirection: "column",
        height: "100% !important",
        backgroundColor: "white !important",
        borderRadius: "8px !important",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1) !important"
    },
    header: {
        padding: "0 20px",
        fontSize: "1.25rem",
    },
    iconHeaderContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        paddingTop: "20px",
        marginBottom: 0,
    },
    icon: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        fontSize: "2.5rem",
        marginBottom: "1.5rem",
        color: dolphinTokens.colorLightBlue,
    },
    description: {
        padding: "10px 20px",
        color: "#869ab8",
        textAlign: "center",
        width: "100%",
    },
    learnMoreLink: {
        marginTop: 'auto !important',
        padding: '0 20px !important',
        marginBottom: '10px !important',
        color: dolphinTokens.colorLightBlue,
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'center',

        "& svg": {
            marginLeft: '5px',
        }
    }
});

interface IServiceCardProps {
    title: string;
    description: string;
    linkUrl: string;
    linkText: string;
    icon: React.ReactNode;
}

const ServiceCard = (props: IServiceCardProps) => {
    const { title, description, linkUrl, linkText, icon } = props;
    const styles = useStyles();

    const headerText = (
        <div className={styles.iconHeaderContainer}>
            <div className={styles.icon}>{icon}</div>
            <Title3 className={styles.header} style={{ textAlign: "center", width: "100%" }}>{title}</Title3>
        </div>
    ),
        descriptionText = <Text as="p" size={400} className={styles.description}>{description}</Text>;

    return (
        <div className={styles.cardCol}>
            <Card className={styles.card}>
                <CardHeader header={headerText} description={descriptionText} />
                <Link className={styles.learnMoreLink} href={linkUrl}>
                    <Text>
                        {linkText}
                    </Text>
                    <ArrowRight16Regular />
                </Link>
            </Card>
        </div>
    );
};

export default function KeyServices() {
    const { t } = useTranslation("helpdesk");
    const styles = useStyles();

    return (
        <div className={styles.row}>
            <ServiceCard
                icon={<Cloud48Regular />}
                title={t("keyServices.remoteSupport.title")}
                description={t("keyServices.remoteSupport.description")}
                linkUrl="/services/cloud"
                linkText={t("keyServices.remoteSupport.lnkLearnMore")}
            />
            <ServiceCard
                icon={<Apps48Regular />}
                title={t("keyServices.networkMonitoring.title")}
                description={t("keyServices.networkMonitoring.description")}
                linkUrl="/services/business-process-solutions"
                linkText={t("keyServices.networkMonitoring.lnkLearnMore")}
            />
            <ServiceCard
                icon={<Shield48Regular />}
                title={t("keyServices.securitySupport.title")}
                description={t("keyServices.securitySupport.description")}
                linkUrl="/services/security"
                linkText={t("keyServices.securitySupport.lnkLearnMore")}
            />
        </div>
    );
}